import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { useKey, usePrevious } from 'react-use';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { Modal } from '@sorare/core/src/atoms/Modal';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useBodyLock } from '@sorare/core/src/hooks/useBodyLock';

import { ChampionshipStory } from '#src/components/championship/ChampionshipStory/index.tsx';
import { CardLeveledUp } from '#src/components/so5/LastFixtureStory/CardLeveledUp/index.tsx';
import { CardRewardsReveal } from '#src/components/so5/LastFixtureStory/CardRewardsReveal/index.tsx';
import { CompletedStreakTask } from '#src/components/so5/LastFixtureStory/CompletedStreakTask/index.tsx';
import { Intro } from '#src/components/so5/LastFixtureStory/Intro/index.tsx';
import { Promotion } from '#src/components/so5/LastFixtureStory/Promotion/index.tsx';
import { Relegation } from '#src/components/so5/LastFixtureStory/Relegation/index.tsx';
import { RewardsRecap } from '#src/components/so5/LastFixtureStory/RewardsRecap/index.tsx';
import { useRewardsVerification } from '#src/components/so5/LastFixtureStory/RewardsRecap/useRewardsVerification.ts';
import type { StoryState } from '#src/components/so5/LastFixtureStory/types.ts';
import { useMarkStoriesAsRead } from '#src/hooks/stories/useMarkStoriesAsRead.ts';

import type {
  RewardsFlow_cardXpGradeThreshold,
  RewardsFlow_currentUser,
  RewardsFlow_leveledUpCard,
  RewardsFlow_so5Fixture,
  RewardsFlow_so5Reward,
  RewardsFlow_thresholdsStreakTask,
  RewardsFlow_userGroupMembershipShopItemsUpdate,
} from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  position: fixed;
  inset: 0;
  overflow: hidden;
  background-color: var(--c-black);
`;

type RewardsFlowProps = {
  storyId?: string;
  currentUser?: RewardsFlow_currentUser | null;
  so5Fixture?: RewardsFlow_so5Fixture;
  so5Rewards: RewardsFlow_so5Reward[];
  leveledUpCards?: RewardsFlow_leveledUpCard[] | null;
  userGroupMembershipShopItemUpdates?:
    | RewardsFlow_userGroupMembershipShopItemsUpdate[]
    | null;
  completedThresholdsStreakTasks?: RewardsFlow_thresholdsStreakTask[] | null;
  gradeThresholds?: RewardsFlow_cardXpGradeThreshold[];
  initialStep?: StoryState;
  onClose?: () => void;
};
export const RewardsFlow = ({
  storyId,
  currentUser,
  so5Fixture,
  so5Rewards: so5RewardsProp,
  leveledUpCards,
  userGroupMembershipShopItemUpdates,
  completedThresholdsStreakTasks,
  gradeThresholds,
  initialStep,
  onClose,
}: RewardsFlowProps) => {
  const [so5RewardsWithSignedAlternative, setSo5RewardsWithSignedAlternative] =
    useState<
      {
        so5RewardId: string;
        signedAlternativeRewardConfigs: string | null;
      }[]
    >([]);
  const [so5Rewards, setSo5Rewards] = useState<RewardsFlow_so5Reward[]>();
  if (!so5Rewards && so5RewardsProp?.length > 0) {
    setSo5Rewards(so5RewardsProp);
  }

  const [open, setOpen] = useState(true);
  const [storyState, setStoryState] = useState<StoryState>(
    initialStep || 'intro'
  );
  const prevStoryState = usePrevious(storyState);
  const { readStories: readFixtureStories, loading: loadingReadStories } =
    useMarkStoriesAsRead();
  const { unclaimedRewardsByType } = useRewardsVerification(so5Rewards || []);

  const finalOnClose = async () => {
    if (storyId && !loadingReadStories) {
      await readFixtureStories({
        storyIds: [storyId],
      });
    }
    setOpen(false);
    onClose?.();
  };
  useKey('Escape', finalOnClose);
  useBodyLock(open);

  const hasUnclaimedReward = unclaimedRewardsByType.total > 0;
  const hasUnclaimedRewardToReveal =
    unclaimedRewardsByType.card.length +
      unclaimedRewardsByType.cash.length +
      unclaimedRewardsByType.shards.length >
    0;

  const hasPromotion = so5Fixture?.mySo5Rankings?.some(
    ranking => ranking.eligibleDivisionChange === DivisionChange.PROMOTED
  );
  const hasRelegation = so5Fixture?.mySo5Rankings?.some(
    ranking => ranking.eligibleDivisionChange === DivisionChange.RELEGATED
  );
  const hasCardLeveledUps = (leveledUpCards?.length || 0) > 0;
  const hasCompletedStreakTasks =
    (completedThresholdsStreakTasks?.length || 0) > 0;
  const hasUserGroupMembershipShopItemUpdates =
    !!userGroupMembershipShopItemUpdates?.length;

  return (
    <Modal>
      <Wrapper>
        {storyState === 'intro' && (
          <Intro
            so5Fixture={so5Fixture}
            so5Rewards={so5Rewards || []}
            onNext={
              hasPromotion ||
              hasRelegation ||
              hasCardLeveledUps ||
              hasUnclaimedRewardToReveal ||
              hasUnclaimedReward ||
              hasCompletedStreakTasks ||
              hasUserGroupMembershipShopItemUpdates
                ? () => {
                    if (hasPromotion) {
                      setStoryState('promotion');
                    } else if (hasRelegation) {
                      setStoryState('relegation');
                    } else if (hasCardLeveledUps) {
                      setStoryState('cardLeveledUp');
                    } else if (hasCompletedStreakTasks) {
                      setStoryState('completedStreakTask');
                    } else if (hasUnclaimedRewardToReveal) {
                      setStoryState('cardReveal');
                    } else if (hasUnclaimedReward) {
                      setStoryState('rewardsRecap');
                    } else if (hasUserGroupMembershipShopItemUpdates) {
                      setStoryState('championshipUpdate');
                    }
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'promotion' && (
          <Promotion
            so5Fixture={so5Fixture}
            onNext={
              hasRelegation ||
              hasCardLeveledUps ||
              hasCompletedStreakTasks ||
              hasUnclaimedRewardToReveal ||
              hasUnclaimedReward ||
              hasUserGroupMembershipShopItemUpdates
                ? () => {
                    if (hasRelegation) {
                      setStoryState('relegation');
                    } else if (hasCardLeveledUps) {
                      setStoryState('cardLeveledUp');
                    } else if (hasCompletedStreakTasks) {
                      setStoryState('completedStreakTask');
                    } else if (hasUnclaimedRewardToReveal) {
                      setStoryState('cardReveal');
                    } else if (hasUnclaimedReward) {
                      setStoryState('rewardsRecap');
                    } else if (hasUserGroupMembershipShopItemUpdates) {
                      setStoryState('championshipUpdate');
                    }
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'relegation' && (
          <Relegation
            from={prevStoryState}
            so5Fixture={so5Fixture}
            onNext={
              hasCardLeveledUps ||
              hasCompletedStreakTasks ||
              hasUnclaimedRewardToReveal ||
              hasUnclaimedReward ||
              hasUserGroupMembershipShopItemUpdates
                ? () => {
                    if (hasCardLeveledUps) {
                      setStoryState('cardLeveledUp');
                    } else if (hasCompletedStreakTasks) {
                      setStoryState('completedStreakTask');
                    } else if (hasUnclaimedRewardToReveal) {
                      setStoryState('cardReveal');
                    } else if (hasUnclaimedReward) {
                      setStoryState('rewardsRecap');
                    } else if (hasUserGroupMembershipShopItemUpdates) {
                      setStoryState('championshipUpdate');
                    }
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'cardLeveledUp' && (
          <CardLeveledUp
            from={prevStoryState}
            so5Fixture={so5Fixture}
            leveledUpCards={leveledUpCards || []}
            gradeThresholds={gradeThresholds}
            onNext={
              hasCompletedStreakTasks ||
              hasUnclaimedRewardToReveal ||
              hasUnclaimedReward ||
              hasUserGroupMembershipShopItemUpdates
                ? () => {
                    if (hasCompletedStreakTasks) {
                      setStoryState('completedStreakTask');
                    } else if (hasUnclaimedRewardToReveal) {
                      setStoryState('cardReveal');
                    } else if (hasUnclaimedReward) {
                      setStoryState('rewardsRecap');
                    } else if (hasUserGroupMembershipShopItemUpdates) {
                      setStoryState('championshipUpdate');
                    }
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'completedStreakTask' && (
          <CompletedStreakTask
            from={prevStoryState}
            so5Fixture={so5Fixture}
            completedStreakTasks={completedThresholdsStreakTasks || []}
            onNext={
              hasUnclaimedRewardToReveal ||
              hasUnclaimedReward ||
              hasUserGroupMembershipShopItemUpdates
                ? () => {
                    if (hasUnclaimedRewardToReveal) {
                      setStoryState('cardReveal');
                    } else if (hasUnclaimedReward) {
                      setStoryState('rewardsRecap');
                    } else if (hasUserGroupMembershipShopItemUpdates) {
                      setStoryState('championshipUpdate');
                    }
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'cardReveal' && (
          <CardRewardsReveal
            so5Fixture={so5Fixture}
            so5Rewards={so5Rewards || []}
            onNext={() => setStoryState('rewardsRecap')}
            onClose={finalOnClose}
            setSo5RewardsWithSignedAlternative={
              setSo5RewardsWithSignedAlternative
            }
          />
        )}
        {storyState === 'rewardsRecap' && (
          <RewardsRecap
            from={prevStoryState}
            so5Fixture={so5Fixture}
            so5Rewards={so5Rewards || []}
            boostedRewardConfigsByRarity={
              currentUser?.myUnclaimedBoostedRewardConfigsByRarity || []
            }
            so5RewardsWithSignedAlternative={so5RewardsWithSignedAlternative}
            onNext={
              hasUserGroupMembershipShopItemUpdates
                ? () => setStoryState('championshipUpdate')
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'championshipUpdate' && (
          <ChampionshipStory
            userGroupMembershipShopItemUpdates={
              userGroupMembershipShopItemUpdates || []
            }
            onClose={finalOnClose}
          />
        )}
      </Wrapper>
    </Modal>
  );
};
RewardsFlow.fragments = {
  userGroupMembershipShopItemsUpdate: gql`
    fragment RewardsFlow_userGroupMembershipShopItemsUpdate on UserGroupMembershipShopItemsUpdate {
      id
      ...ChampionshipStory_userGroupMembershipShopItemsUpdate
    }
    ${ChampionshipStory.fragments.userGroupMembershipShopItemsUpdate}
  ` as TypedDocumentNode<RewardsFlow_userGroupMembershipShopItemsUpdate>,
  currentUser: gql`
    fragment RewardsFlow_currentUser on CurrentUser {
      slug
      myUnclaimedBoostedRewardConfigsByRarity(sport: $sport) {
        ...RewardsRecap_rarityRewardConfigs
      }
    }
    ${RewardsRecap.fragments.rarityRewardConfigs}
  ` as TypedDocumentNode<RewardsFlow_currentUser>,
  thresholdsStreakTask: gql`
    fragment RewardsFlow_thresholdsStreakTask on ThresholdsStreakTask {
      id
      ...CompletedStreakTask_thresholdsStreakTask
    }
    ${CompletedStreakTask.fragments.thresholdsStreakTask}
  ` as TypedDocumentNode<RewardsFlow_thresholdsStreakTask>,
  cardXpGradeThreshold: gql`
    fragment RewardsFlow_cardXpGradeThreshold on CardXPGradeThreshold {
      ...CardLeveledUp_cardXpGradeThreshold
    }
    ${CardLeveledUp.fragments.cardXpGradeThreshold}
  ` as TypedDocumentNode<RewardsFlow_cardXpGradeThreshold>,
  leveledUpCard: gql`
    fragment RewardsFlow_leveledUpCard on LeveledUpCard {
      id
      ...CardLeveledup_leveledUpCard
    }
    ${CardLeveledUp.fragments.leveledUpCard}
  ` as TypedDocumentNode<RewardsFlow_leveledUpCard>,
  so5Reward: gql`
    fragment RewardsFlow_so5Reward on So5Reward {
      slug
      aasmState
      ...Intro_so5Reward
      ...RewardsRecap_so5Reward
      ...CardRewardsReveal_so5Reward
      ...useRewardsVerification_so5Reward
    }
    ${Intro.fragments.so5Reward}
    ${RewardsRecap.fragments.so5Reward}
    ${useRewardsVerification.fragments.so5Reward}
    ${CardRewardsReveal.fragments.so5Reward}
  ` as TypedDocumentNode<RewardsFlow_so5Reward>,
  so5Fixture: gql`
    fragment RewardsFlow_so5Fixture on So5Fixture {
      slug
      sport
      mySo5Rankings {
        id
        eligibleDivisionChange
      }
      ...Intro_so5Fixture
      ...Promotion_so5Fixture
      ...Relegation_so5Fixture
      ...CardLeveledUp_so5Fixture
      ...CompletedStreakTask_so5Fixture
      ...RewardsRecap_so5Fixture
      ...CardRewardsReveal_so5Fixture
    }
    ${Intro.fragments.so5Fixture}
    ${Promotion.fragments.so5Fixture}
    ${Relegation.fragments.so5Fixture}
    ${CardLeveledUp.fragments.so5Fixture}
    ${CompletedStreakTask.fragments.so5Fixture}
    ${RewardsRecap.fragments.so5Fixture}
    ${CardRewardsReveal.fragments.so5Fixture}
  ` as TypedDocumentNode<RewardsFlow_so5Fixture>,
};
